/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/**
 * How a vehicle context was opened. Shared so the explorer and usage analytics
 * agree, rather than each inferring it from the URL independently.
 */
export type EntrySource = 'vin' | 'vehicle-search' | 'program-search';

export const ENTRY_SOURCES: EntrySource[] = ['vin', 'vehicle-search', 'program-search'];

/**
 * The query parameter each launcher stamps to say which search it was.
 *
 * Inferring it from the identity parameters is only a guess: a VIN launch and a
 * vehicle search resolving to the same program differ only in carrying `vin`
 * versus `make`.
 */
export const ENTRY_SOURCE_PARAM = 'src';

/** Reads the stamped marker, or `null` for a URL written before it existed. */
export function readEntrySource(raw: string | null | undefined): EntrySource | null {
  const value = (raw ?? '').trim().toLowerCase();
  return (ENTRY_SOURCES as string[]).includes(value) ? (value as EntrySource) : null;
}

/**
 * How a view was opened, for the info fields, the header identity and usage analytics.
 *
 * The launchers stamp the answer, so a stamped URL is taken at its word. Links written before the
 * marker existed, and the deep links other tools build by hand, are read from their identity
 * params instead: a VIN launch is the only gesture carrying `vin`, and a vehicle search the only
 * one carrying both `make` and `model`.
 *
 * Model year is no part of that test, though it used to be. A program search carries `year` too,
 * so every one of them was read as a vehicle search and shown Make and Model — two fields it does
 * not have — in place of the program code it does.
 */
export function resolveEntrySource(get: (key: string) => string | null): EntrySource {
  const stamped = readEntrySource(get(ENTRY_SOURCE_PARAM));
  if (stamped) return stamped;
  // A blank param names nothing. Every reader trims these before use, so a `make=%20` that counted
  // as a vehicle search would promise a Make and Model field with nothing to put in it.
  const has = (key: string) => Boolean(get(key)?.trim());
  if (has('vin') || has('VIN')) return 'vin';
  if (has('make') && has('model')) return 'vehicle-search';
  return 'program-search';
}

/**
 * Whether the entry gesture named a vehicle, and so whether a make and model may be shown for it.
 *
 * A program search names a program. Catalog rows that share its code carry a make and model, but
 * they belong to the vehicles built on that program, not to a vehicle the user picked, so stating
 * them would answer a question that was never asked.
 */
export function namesVehicle(source: EntrySource): boolean {
  return source !== 'program-search';
}
